/**
 * Characters picture-tier vignettes (AUTHORING.md v3): one painted background per picture (public/vignettes/characters/<bg>.webp,
 * prompts docs/vignette-prompts.py, CREDITS.md) + the picture's posed resident from core (`content/worlds/characters/pictures.ts`).
 * `elements` = every part the painting shows (each one is drawn + coloured in the picture lesson); `overlay` = taught
 * props the painting lacks (drawn over it).
 */
import type { VignetteSpec, WorldVignettes } from '../../vignette/types';

const v = (id: string, elements: string[], overlay?: string[]): VignetteSpec => ({ id: `${id}-picture`, bg: id, elements, ...(overlay ? { overlay } : {}) });

const specs: VignetteSpec[] = [
  /* L1 · toy square */
  v('characters-happy-face', ['sky', 'ground', 'houses', 'bunting']),
  v('characters-robot', ['sky', 'ground', 'shelf', 'window']),
  v('characters-remix-face-surprised', ['sky', 'ground', 'reeds', 'pond']),
  v('characters-remix-face-sleepy', ['sky', 'ground', 'window', 'books']),
  v('characters-remix-face-giggle', ['sky', 'ground', 'sunflowers']),
  /* L2 · rocket park */
  v('characters-astronaut', ['sky', 'stars', 'ground', 'rocket']),
  v('characters-wizard', ['sky', 'moon', 'stars', 'ground', 'ferns', 'mushrooms']),
  v('characters-firefighter', ['sky', 'ground', 'station', 'hydrant']),
  v('characters-remix-astronaut-flag', ['sky', 'earth', 'moon', 'stars', 'ground']),
  v('characters-remix-astronaut-star', ['sky', 'stars', 'hills', 'ground', 'ferns']),
  v('characters-remix-astronaut-cat-helmet', ['sky', 'nebula', 'planet', 'stars', 'ground']),
  /* L3 · harbour street */
  v('characters-pirate', ['sky', 'ground', 'sea', 'crates', 'pier', 'buoys']),
  v('characters-chef', ['sky', 'ground', 'shelf', 'window']),
  v('characters-painter', ['sky', 'ground', 'easel', 'trees', 'flowers']),
  v('characters-remix-chef-space', ['sky', 'planet', 'stars', 'ground', 'dome', 'craters']),
  v('characters-remix-chef-cake', ['sky', 'ground', 'bunting', 'balloons']),
  v('characters-remix-chef-robot', ['sky', 'ground', 'oven', 'pot']),
  v('characters-remix-pirate-parrot', ['sky', 'sea', 'island', 'ground', 'palm', 'shells']),
  v('characters-remix-pirate-braids', ['sky', 'cliffs', 'ground']),
  v('characters-remix-pirate-map', ['sky', 'ground', 'rocks', 'chest']),
  /* L4 · hero stadium */
  v('characters-superhero', ['sky', 'clouds', 'ground', 'roofs']),
  v('characters-runner', ['sky', 'ground', 'stands', 'bunting']),
  v('characters-dancer', ['sky', 'spotlights', 'ground', 'curtains']),
  v('characters-remix-superhero-flying', ['sky', 'clouds', 'fields']),
  v('characters-remix-superhero-landing', ['sky', 'ground', 'path', 'trees', 'bench']),
  v('characters-remix-superhero-cat-hero', ['sky', 'moon', 'fence', 'trees', 'ground']),
  v('characters-remix-runner-long-jump', ['sky', 'ground', 'stands']),
  v('characters-remix-runner-relay', ['sky', 'ground', 'trees', 'cones']),
  v('characters-remix-runner-skates', ['sky', 'sea', 'ground', 'lamps']),
  /* playground-only */
  v('characters-block-buddy', ['sky', 'ground', 'blocks']),
];

export const CHARACTERS_VIGNETTES: WorldVignettes = { world: 'characters', specs };
